import { FileRouteTypes } from "@/routeTree.gen";
import { create } from "zustand";

export type NotificationIntent = "warning" | "info" | "error" | "success";

export type Notification = {
  id: string;
  title?: string;
  message: string;
  url?: FileRouteTypes["to"];
  params?: Record<string, string | number | boolean | undefined>;
  onClick?: () => void;
  intent: NotificationIntent;
  /**
   * Whether the notification can be removed by the user. If false, it will remain until the underlying issue is resolved.
   * Close button will not be shown for non-removable notifications, and they will not be dismissed when marked as read.
   */
  removable?: boolean;
  /**
   * Whether the notification has been read by the user.
   * Notifications with a url are marked as read when the user clicks the notification link.
   * Non-removable notifications stay visible even when read.
   */
  read?: boolean;
};

interface PlatformNotificationsState {
  notifications: Notification[];
  hasUnread: boolean;
  hasRemovable: boolean;
  setWarnings: (warnings: Notification[]) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  removeAllNotifications: () => void;
}

const STORAGE_READ_KEY = "platform-notifications-read";
const STORAGE_REMOVED_KEY = "platform-notifications-removed";

const getStoredIds = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) ?? "[]"));
  } catch {
    return new Set();
  }
};

const saveStoredIds = (key: string, ids: Set<string>): void => {
  localStorage.setItem(key, JSON.stringify([...ids]));
};

const derivedState = (notifications: Notification[]) => ({
  hasUnread: notifications.some((n) => !n.read),
  hasRemovable: notifications.some((n) => n.removable !== false),
});

const applyStoredState = (warnings: Notification[], readIds: Set<string>, removedIds: Set<string>): Notification[] =>
  warnings.filter((n) => !removedIds.has(n.id)).map((n) => ({ ...n, read: readIds.has(n.id) }));

const createPlatformNotificationsStore = (
  set: (state: Partial<PlatformNotificationsState>) => void,
  get: () => PlatformNotificationsState,
): PlatformNotificationsState => ({
  notifications: [],
  hasUnread: false,
  hasRemovable: false,

  setWarnings: (warnings) => {
    const readIds = getStoredIds(STORAGE_READ_KEY);
    const removedIds = getStoredIds(STORAGE_REMOVED_KEY);
    const notifications = applyStoredState(warnings, readIds, removedIds);
    set({ notifications, ...derivedState(notifications) });
  },

  markAsRead: (id) => {
    const readIds = getStoredIds(STORAGE_READ_KEY);
    readIds.add(id);
    saveStoredIds(STORAGE_READ_KEY, readIds);
    const notifications = get().notifications.map((n) => (n.id === id ? { ...n, read: true } : n));
    set({ notifications, ...derivedState(notifications) });
  },

  markAllAsRead: () => {
    const readIds = getStoredIds(STORAGE_READ_KEY);
    const notifications = get().notifications.map((n) => {
      readIds.add(n.id);
      return { ...n, read: true };
    });
    saveStoredIds(STORAGE_READ_KEY, readIds);
    set({ notifications, ...derivedState(notifications) });
  },

  removeNotification: (id) => {
    const removedIds = getStoredIds(STORAGE_REMOVED_KEY);
    removedIds.add(id);
    saveStoredIds(STORAGE_REMOVED_KEY, removedIds);
    const notifications = get().notifications.filter((n) => n.id !== id);
    set({ notifications, ...derivedState(notifications) });
  },

  removeAllNotifications: () => {
    const removedIds = getStoredIds(STORAGE_REMOVED_KEY);
    const notifications = get().notifications.filter((n) => {
      if (n.removable !== false) removedIds.add(n.id);
      return n.removable === false;
    });
    saveStoredIds(STORAGE_REMOVED_KEY, removedIds);
    set({ notifications, ...derivedState(notifications) });
  },
});

export const usePlatformNotificationsStore = create<PlatformNotificationsState>(createPlatformNotificationsStore);
